import BodyText from '@lyra/core/components/BodyText'
import { createColumnHelper, TableColumnDef, TableData } from '@lyra/core/components/Table'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatTruncatedNumber from '@lyra/core/utils/formatTruncatedNumber'
import formatTruncatedUSD from '@lyra/core/utils/formatTruncatedUSD'
import formatUSD from '@lyra/core/utils/formatUSD'
import { Board, Ticker } from '@lyra/web/constants/instruments'
import { MarketId } from '@lyra/web/constants/markets'
import useSelectedInstruments from '@lyra/web/hooks/useSelectedInstruments'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import useUserSettings from '@lyra/web/hooks/useUserSettings'
import { getBestAskPrice, getBestBidPrice } from '@lyra/web/utils/instruments'
import { getOptionBreakEvenPrice } from '@lyra/web/utils/order'
import {
  isOptionsChainPreset,
  OptionsChainColumn,
  OptionsChainDisplay,
  OptionsChainPreset,
  OptionsChainSettings,
} from '@lyra/web/utils/userSettings'
import React, { useCallback, useMemo } from 'react'
import { StackProps } from 'tamagui'

import TablePriceCell from './TablePriceCell'
import TradeOptionTableCallAndPut from './TradeOptionTableCallAndPut'
import TradeOptionTableCallOrPut from './TradeOptionTableCallOrPut'

const MARK_PRICE_COLUMN_WIDTH = 75
const BID_ASK_SIZE_COLUMN_WIDTH = 60
const BID_ASK_IV_COLUMN_WIDTH = 55

const getColumnWidth = (col: OptionsChainColumn): number => {
  switch (col) {
    // raw data 2dp, e.g. +2.50
    case OptionsChainColumn.Delta:
    case OptionsChainColumn.Vega:
    case OptionsChainColumn.Gamma:
    case OptionsChainColumn.Theta:
    case OptionsChainColumn.Rho:
    case OptionsChainColumn.Position:
      return 35
    // extra space for $ or % prefix
    case OptionsChainColumn.OI:
    case OptionsChainColumn.Volume:
    case OptionsChainColumn.Change:
    case OptionsChainColumn.IV:
      return 45
    case OptionsChainColumn.PctChange:
      return 60
    case OptionsChainColumn.BreakEven:
      return 65
    // long title
    case OptionsChainColumn.ToBreakEven:
      return 90
  }
}

type TradeOptionTableProps = {
  marketId: MarketId
  optionTable: OptionsChainDisplay
  selectedBoard: Board
} & StackProps

export const renderCustomColumn = (
  row: TradeOptionTableTickerData,
  isCall: boolean,
  customCol: OptionsChainColumn,
  settings: OptionsChainSettings
): React.ReactNode | string | null => {
  const ticker = isCall ? row.call : row.put
  const position = isCall ? row.callPosition : row.putPosition

  if (!ticker) {
    return null
  }

  const breakEven = +ticker.mark_price
    ? getOptionBreakEvenPrice(ticker.instrument_name, +ticker.mark_price)
    : 0
  const spotPrice = +ticker.index_price
  const breakEvenPctDiff = spotPrice > 0 ? (breakEven - spotPrice) / spotPrice : 0
  const toBreakEven = breakEven
    ? isCall
      ? spotPrice > breakEven
        ? 0
        : breakEvenPctDiff
      : spotPrice < breakEven
      ? 0
      : breakEvenPctDiff
    : null

  const isSimplePreset = isOptionsChainPreset(settings) === OptionsChainPreset.Simple

  // Note: this is an approximation of notional volume using spot price, round to nearest thousand to avoid flickering
  // TODO: @earthtojake use aggregate notional volume from orderbook endpoint
  const volume = Math.ceil((+ticker.stats.contract_volume * spotPrice) / 1000) * 1000

  switch (customCol) {
    case OptionsChainColumn.Delta:
      return formatNumber(+ticker.option_pricing.delta, { showSign: true, dps: 2 })
    case OptionsChainColumn.OI:
      return formatTruncatedNumber(+ticker.stats.open_interest)
    case OptionsChainColumn.Volume:
      return volume ? formatTruncatedUSD(volume) : null
    case OptionsChainColumn.Vega:
      return formatNumber(+ticker.option_pricing.vega, { showSign: true, dps: 2 })
    case OptionsChainColumn.Gamma:
      return formatNumber(+ticker.option_pricing.gamma, { showSign: true, dps: 2 })
    case OptionsChainColumn.Theta:
      return formatNumber(+ticker.option_pricing.theta, { showSign: true, dps: 2 })
    case OptionsChainColumn.Rho:
      return formatNumber(+ticker.option_pricing.rho, { showSign: true, dps: 2 })
    case OptionsChainColumn.IV:
      return +ticker.option_pricing.iv
        ? formatPercentage(+ticker.option_pricing.iv, { dps: 1 })
        : null
    case OptionsChainColumn.Position:
      return position ? formatNumber(position, { dps: 2 }) : null
    case OptionsChainColumn.BreakEven:
      return breakEven ? formatUSD(breakEven, { dps: isSimplePreset ? 0 : 2 }) : null
    case OptionsChainColumn.ToBreakEven:
      return toBreakEven ? formatPercentage(toBreakEven, { dps: 2, showSign: true }) : null
    case OptionsChainColumn.Change:
      return +ticker.stats.usd_change
        ? formatUSD(+ticker.stats.usd_change, { showSign: true })
        : null
    case OptionsChainColumn.PctChange:
      return +ticker.stats.percent_change
        ? formatPercentage(+ticker.stats.percent_change, { dps: 2, showSign: true })
        : null
  }
}

export const getCustomColumnLabel = (columnOption: OptionsChainColumn): string => {
  switch (columnOption) {
    case OptionsChainColumn.Delta:
      return 'Delta'
    case OptionsChainColumn.OI:
      return 'Open'
    case OptionsChainColumn.Volume:
      return 'Volume'
    case OptionsChainColumn.Vega:
      return 'Vega'
    case OptionsChainColumn.Gamma:
      return 'Gamma'
    case OptionsChainColumn.Theta:
      return 'Theta'
    case OptionsChainColumn.Rho:
      return 'Rho'
    case OptionsChainColumn.IV:
      return 'IV'
    case OptionsChainColumn.Position:
      return 'Position'
    case OptionsChainColumn.BreakEven:
      return 'Breakeven'
    case OptionsChainColumn.ToBreakEven:
      return 'To Breakeven'
    case OptionsChainColumn.Change:
      return 'Change'
    case OptionsChainColumn.PctChange:
      return '% Change'
  }
}

export type TradeOptionTableTickerData = TableData<{
  id: string
  marketId: MarketId
  callPosition: number
  putPosition: number
  call: Ticker | null
  put: Ticker | null
  strikePrice: number
}>

const TradeOptionTable = ({
  marketId,
  optionTable,
  selectedBoard,
  ...tableProps
}: TradeOptionTableProps): JSX.Element => {
  const { userSettings } = useUserSettings()
  const columnHelper = createColumnHelper<TradeOptionTableTickerData>()

  const { subaccount } = useSubaccount()
  const { toggleInstrumentDirection, getIsInstrumentWithDirectionSelected } =
    useSelectedInstruments()

  const getCustomColumns = useCallback(
    (isCall: boolean) =>
      userSettings.optionsChain.columns.map((col: OptionsChainColumn) =>
        columnHelper.accessor('strikePrice', {
          id: `${isCall ? 'c' : 'p'}_${col}`,
          meta: {
            minWidth: getColumnWidth(col),
          },
          header: getCustomColumnLabel(col),
          cell: (props) => {
            const customColumn = renderCustomColumn(
              props.cell.row.original,
              isCall,
              col,
              userSettings.optionsChain
            )
            if (typeof customColumn === 'string') {
              return <BodyText>{customColumn}</BodyText>
            } else {
              return customColumn
            }
          },
        })
      ) as TableColumnDef<TradeOptionTableTickerData>[],
    [columnHelper, userSettings.optionsChain]
  )

  const { showBidAskIv, showBidAskSize, showMarkPrice } = userSettings.optionsChain

  const getPriceColumns = useCallback(
    (isCall: boolean) =>
      filterNulls([
        showBidAskIv
          ? columnHelper.accessor('strikePrice', {
              id: `${isCall ? 'c' : 'p'}_bid_iv`,
              meta: {
                minWidth: BID_ASK_IV_COLUMN_WIDTH,
                noPadding: true,
              },
              header: 'Bid IV',
              cell: (props) => {
                const ticker = isCall ? props.row.original.call : props.row.original.put
                const iv = ticker ? +ticker.option_pricing.bid_iv : undefined
                const isValid = !!ticker && !!getBestBidPrice(ticker)
                return (
                  <BodyText color={iv ? 'primary' : 'secondary'}>
                    {iv && isValid ? formatPercentage(iv, { dps: 2 }) : '—'}
                  </BodyText>
                )
              },
            })
          : null,
        showBidAskSize
          ? columnHelper.accessor('strikePrice', {
              id: `${isCall ? 'c' : 'p'}_bid_size`,
              meta: {
                minWidth: BID_ASK_SIZE_COLUMN_WIDTH,
                noPadding: true,
              },
              header: 'Bid Size',
              cell: (props) => {
                const ticker = isCall ? props.row.original.call : props.row.original.put
                const size = ticker ? +ticker.best_bid_amount : undefined
                const isValid = !!ticker && !!getBestBidPrice(ticker)
                return (
                  <BodyText color={size ? 'primary' : 'secondary'}>
                    {size && isValid ? formatNumber(size, { dps: 1 }) : '—'}
                  </BodyText>
                )
              },
            })
          : null,
        columnHelper.accessor('strikePrice', {
          id: `${isCall ? 'c' : 'p'}_bid`,
          meta: {
            minWidth: 85,
            noPadding: true,
          },
          header: 'Bid Price',
          cell: (props) => (
            <TablePriceCell
              {...props}
              isCall={isCall}
              isBid={true}
              isLarge={optionTable !== 'all'}
              tickSize={+((props.row.original.call ?? props.row.original.put)?.tick_size ?? 0)}
              onPress={() => {
                const ticker = isCall ? props.row.original.call : props.row.original.put
                if (ticker) {
                  toggleInstrumentDirection({
                    instrument_name: ticker.instrument_name,
                    direction: 'sell',
                  })
                }
              }}
              isSelected={
                isCall && props.row.original.call
                  ? getIsInstrumentWithDirectionSelected({
                      instrument_name: props.row.original.call.instrument_name,
                      direction: 'sell',
                    })
                  : !isCall && props.row.original.put
                  ? getIsInstrumentWithDirectionSelected({
                      instrument_name: props.row.original.put.instrument_name,
                      direction: 'sell',
                    })
                  : false
              }
            />
          ),
        }),
        showMarkPrice
          ? columnHelper.accessor('strikePrice', {
              id: `${isCall ? 'c' : 'p'}_mark_price`,
              meta: {
                minWidth: MARK_PRICE_COLUMN_WIDTH,
                noPadding: true,
              },
              header: 'Mark Price',
              cell: (props) => {
                const ticker = isCall ? props.row.original.call : props.row.original.put
                const markPrice = ticker ? +ticker.mark_price : undefined
                return <BodyText>{markPrice ? formatUSD(markPrice, { dps: 2 }) : '—'}</BodyText>
              },
            })
          : null,
        columnHelper.accessor('strikePrice', {
          id: `${isCall ? 'c' : 'p'}_ask`,
          meta: {
            minWidth: 85,
            noPadding: true,
          },
          header: 'Ask Price',
          cell: (props) => (
            <TablePriceCell
              {...props}
              isCall={isCall}
              isBid={false}
              isLarge={optionTable !== 'all'}
              tickSize={+((props.row.original.call ?? props.row.original.put)?.tick_size ?? 0)}
              onPress={() => {
                const ticker = isCall ? props.row.original.call : props.row.original.put
                if (ticker) {
                  toggleInstrumentDirection({
                    instrument_name: ticker.instrument_name,
                    direction: 'buy',
                  })
                }
              }}
              isSelected={
                isCall && props.row.original.call
                  ? getIsInstrumentWithDirectionSelected({
                      instrument_name: props.row.original.call.instrument_name,
                      direction: 'buy',
                    })
                  : !isCall && props.row.original.put
                  ? getIsInstrumentWithDirectionSelected({
                      instrument_name: props.row.original.put.instrument_name,
                      direction: 'buy',
                    })
                  : false
              }
            />
          ),
        }),
        showBidAskSize
          ? columnHelper.accessor('strikePrice', {
              id: `${isCall ? 'c' : 'p'}_ask_size`,
              meta: {
                minWidth: BID_ASK_SIZE_COLUMN_WIDTH,
                noPadding: true,
              },
              header: 'Ask Size',
              cell: (props) => {
                const ticker = isCall ? props.row.original.call : props.row.original.put
                const size = ticker ? +ticker.best_ask_amount : undefined
                const isValid = !!ticker && !!getBestAskPrice(ticker)
                return (
                  <BodyText color={size ? 'primary' : 'secondary'}>
                    {size && isValid ? formatNumber(size, { dps: 1 }) : '—'}
                  </BodyText>
                )
              },
            })
          : null,
        showBidAskIv
          ? columnHelper.accessor('strikePrice', {
              id: `${isCall ? 'c' : 'p'}_ask_iv`,
              meta: {
                minWidth: BID_ASK_IV_COLUMN_WIDTH,
                noPadding: true,
              },
              header: 'Ask IV',
              cell: (props) => {
                const ticker = isCall ? props.row.original.call : props.row.original.put
                const iv = ticker ? +ticker.option_pricing.ask_iv : undefined
                const isValid = !!ticker && !!getBestAskPrice(ticker)
                return (
                  <BodyText color={iv ? 'primary' : 'secondary'}>
                    {iv && isValid ? formatPercentage(iv, { dps: 2 }) : '—'}
                  </BodyText>
                )
              },
            })
          : null,
      ]) as TableColumnDef<TradeOptionTableTickerData>[],
    [
      showBidAskIv,
      columnHelper,
      showBidAskSize,
      showMarkPrice,
      optionTable,
      toggleInstrumentDirection,
      getIsInstrumentWithDirectionSelected,
    ]
  )

  const callColumns = useMemo(
    () => [...getCustomColumns(true), ...getPriceColumns(true)],
    [getCustomColumns, getPriceColumns]
  )

  const putColumns = useMemo(
    () =>
      optionTable === 'all'
        ? [...getPriceColumns(false), ...getCustomColumns(false).reverse()]
        : [...getCustomColumns(false), ...getPriceColumns(false)],
    [optionTable, getCustomColumns, getPriceColumns]
  )

  const positionsMap = useMemo(() => {
    return (
      subaccount?.positions.reduce(
        (map, position) => ({
          ...map,
          [position.instrument_name]: +position.amount,
        }),
        {} as Record<string, number>
      ) ?? {}
    )
  }, [subaccount])

  const rows: TradeOptionTableTickerData[] = useMemo(
    () =>
      Object.values(selectedBoard.strikes)
        .map((strike) => {
          const row: TradeOptionTableTickerData = {
            id: (strike.call ?? strike.put)?.option_details.strike ?? 'unknown',
            marketId,
            call: strike.call,
            callPosition: positionsMap[strike.call?.instrument_name ?? ''] ?? 0,
            put: strike.put,
            putPosition: positionsMap[strike.put?.instrument_name ?? ''] ?? 0,
            strikePrice: +((strike.call || strike.put)?.option_details.strike ?? 0),
          }
          return row
        })
        .sort((a, b) => a.strikePrice - b.strikePrice),
    [selectedBoard.strikes, marketId, positionsMap]
  )

  return optionTable === 'all' ? (
    <TradeOptionTableCallAndPut
      {...tableProps}
      selectedMarket={marketId}
      selectedBoard={selectedBoard}
      callColumns={callColumns}
      putColumns={putColumns}
      rows={rows}
    />
  ) : (
    <TradeOptionTableCallOrPut
      {...tableProps}
      selectedMarket={marketId}
      isCalls={optionTable === 'calls'}
      columns={optionTable === 'calls' ? callColumns : putColumns}
      rows={rows}
    />
  )
}

export default TradeOptionTable
